import { ProfileTemplatePublishStatus } from '@up2stay/up2promo-api-client';
import { graphemeLength } from '@up2stay/up2promo-commons';
import {
  Badge,
  Button,
  Textarea,
  cn,
  healthAttentionSurfaceClass,
  healthOkSurfaceClass,
} from '@up2stay/up2promo-ui';
import { CheckCircle2, Loader2, PencilLine, Undo2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  TemplateOverviewField,
  TemplateOverviewSection,
} from '@/core/components/profile-templates/template-overview-section';
import {
  TEMPLATE_DETAIL_MUTED_CLASS,
  TemplateDetailSectionCard,
} from '@/core/components/profile-templates/template-detail-section-card';
import { TemplateTeamsAssignmentCard } from '@/core/components/profile-templates/template-teams-assignment-card';
import { MAX_TEMPLATE_NAME } from '@/core/components/profile-templates/template-name';
import { InputWithCharCount } from '@/core/components/ui/field-with-char-count';

export type ProfileTemplateDetailOverviewDict = {
  generalTitle: string;
  generalDescription?: string;
  nameLabel: string;
  namePlaceholder: string;
  nameRequired: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  publish: string;
  publishing: string;
  discardChanges: string;
  discardingChanges: string;
  publishStatus: {
    draft: string;
    published: string;
    syncing: string;
    draftHint: string;
    syncingHint: string;
    publishedHint: string;
    draftHintMobile: string;
    syncingHintMobile: string;
    publishedHintMobile: string;
  };
  detailTeams: {
    title: string;
    description: string;
    emptyTeams: string;
    assignedCount: string;
    selectionCount?: string;
    noSelection?: string;
    members?: string;
  };
  liveRevisionLabel?: string;
  viewRevisionHistory?: string;
};

type ProfileTemplateDetailOverviewProps = {
  name: string;
  description: string;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  canManage: boolean;
  publishStatus: ProfileTemplatePublishStatus;
  publishStatusLabel: string;
  publishing: boolean;
  discardingChanges: boolean;
  onPublish: () => void;
  onDiscardChanges: () => void;
  workspaceId: string;
  templateTeamIds: string[];
  onTeamIdsChange: (next: string[]) => void;
  dict: ProfileTemplateDetailOverviewDict;
  liveRevision?: number | null;
  onViewRevisionHistory?: () => void;
};

function publishStatusBadgeVariant(
  status: ProfileTemplatePublishStatus,
): 'success' | 'warning' | 'secondary' {
  switch (status) {
    case ProfileTemplatePublishStatus.PUBLISHED:
      return 'success';
    case ProfileTemplatePublishStatus.SYNCING:
      return 'secondary';
    default:
      return 'warning';
  }
}

function publishBannerClass(status: ProfileTemplatePublishStatus): string {
  switch (status) {
    case ProfileTemplatePublishStatus.SYNCING:
      return 'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)]/40';
    case ProfileTemplatePublishStatus.DRAFT:
      return healthAttentionSurfaceClass;
    case ProfileTemplatePublishStatus.PUBLISHED:
      return healthOkSurfaceClass;
    default:
      return '';
  }
}

const LG_TWO_COLUMN_MQ = '(min-width: 1024px)';

export function ProfileTemplateDetailOverview({
  name,
  description,
  onNameChange,
  onDescriptionChange,
  canManage,
  publishStatus,
  publishStatusLabel,
  publishing,
  discardingChanges,
  onPublish,
  onDiscardChanges,
  workspaceId,
  templateTeamIds,
  onTeamIdsChange,
  dict,
  liveRevision = null,
  onViewRevisionHistory,
}: ProfileTemplateDetailOverviewProps) {
  const nameMissing = canManage && !name.trim();
  const stammdatenCardRef = useRef<HTMLDivElement>(null);
  const [stammdatenCardHeight, setStammdatenCardHeight] = useState<
    number | undefined
  >();
  const [lgTwoColumn, setLgTwoColumn] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia(LG_TWO_COLUMN_MQ).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(LG_TWO_COLUMN_MQ);
    const onChange = () => setLgTwoColumn(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!lgTwoColumn) return;

    const el = stammdatenCardRef.current;
    if (!el) return;

    const updateHeight = () => {
      setStammdatenCardHeight(el.getBoundingClientRect().height);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [lgTwoColumn, name, description, dict.generalDescription]);

  const showPublishButton =
    canManage &&
    (publishStatus === ProfileTemplatePublishStatus.DRAFT ||
      publishStatus === ProfileTemplatePublishStatus.SYNCING);

  const showDiscardButton =
    canManage && publishStatus === ProfileTemplatePublishStatus.DRAFT;

  const statusHints =
    publishStatus === ProfileTemplatePublishStatus.SYNCING
      ? {
          full: dict.publishStatus.syncingHint,
          mobile: dict.publishStatus.syncingHintMobile,
        }
      : publishStatus === ProfileTemplatePublishStatus.DRAFT
        ? {
            full: dict.publishStatus.draftHint,
            mobile: dict.publishStatus.draftHintMobile,
          }
        : {
            full: dict.publishStatus.publishedHint,
            mobile: dict.publishStatus.publishedHintMobile,
          };

  return (
    <div className="flex min-w-0 w-full flex-col gap-4">
      <TemplateDetailSectionCard
        className={cn('min-w-0 w-full', publishBannerClass(publishStatus))}
        contentClassName="gap-4"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-col gap-2">
            <Badge
              variant={publishStatusBadgeVariant(publishStatus)}
              className={cn(
                'w-fit gap-1.5',
                publishStatus === ProfileTemplatePublishStatus.SYNCING &&
                  'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)] text-[color:var(--ghost-accent-fg)]',
              )}
            >
              {publishStatus === ProfileTemplatePublishStatus.SYNCING ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : publishStatus === ProfileTemplatePublishStatus.PUBLISHED ? (
                <CheckCircle2 className="h-3.5 w-3.5" />
              ) : (
                <PencilLine className="h-3.5 w-3.5" />
              )}
              {publishStatusLabel}
            </Badge>
            <p className={cn(TEMPLATE_DETAIL_MUTED_CLASS, 'sm:hidden')}>
              {statusHints.mobile}
            </p>
            <p className={cn(TEMPLATE_DETAIL_MUTED_CLASS, 'hidden sm:block')}>
              {statusHints.full}
            </p>
            {liveRevision != null &&
            dict.liveRevisionLabel &&
            onViewRevisionHistory ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13.5px]">
                <span className="text-muted-foreground">
                  {dict.liveRevisionLabel.replace(
                    '{revision}',
                    String(liveRevision),
                  )}
                </span>
                <Button
                  type="button"
                  variant="ghostAccent"
                  size="sm"
                  className="h-auto p-0 text-[13.5px]"
                  onClick={onViewRevisionHistory}
                >
                  {dict.viewRevisionHistory}
                </Button>
              </div>
            ) : null}
          </div>

          {showDiscardButton || showPublishButton ? (
            <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
              {showDiscardButton ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  disabled={discardingChanges || publishing}
                  onClick={onDiscardChanges}
                >
                  {discardingChanges ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {dict.discardingChanges}
                    </>
                  ) : (
                    <>
                      <Undo2 className="mr-2 h-4 w-4" />
                      {dict.discardChanges}
                    </>
                  )}
                </Button>
              ) : null}
              {showPublishButton ? (
                <Button
                  type="button"
                  size="sm"
                  className="w-full sm:w-auto"
                  disabled={
                    publishing ||
                    discardingChanges ||
                    publishStatus === ProfileTemplatePublishStatus.SYNCING ||
                    !name.trim()
                  }
                  onClick={onPublish}
                >
                  {publishing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {dict.publishing}
                    </>
                  ) : (
                    dict.publish
                  )}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </TemplateDetailSectionCard>

      <div className="grid min-w-0 gap-4 lg:grid-cols-2">
        <div ref={stammdatenCardRef} className="min-w-0 self-start">
          <TemplateOverviewSection
            icon={PencilLine}
            title={dict.generalTitle}
            description={dict.generalDescription}
          >
            <div className="flex flex-col gap-4">
              <TemplateOverviewField id="template-name" label={dict.nameLabel}>
                <InputWithCharCount
                  id="template-name"
                  value={name}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder={dict.namePlaceholder}
                  disabled={!canManage}
                  className="min-w-0 w-full"
                  maxLength={MAX_TEMPLATE_NAME}
                  valueLength={graphemeLength(name)}
                  aria-invalid={nameMissing || undefined}
                  aria-describedby={
                    nameMissing ? 'template-name-error' : undefined
                  }
                />
                {nameMissing ? (
                  <p
                    id="template-name-error"
                    role="alert"
                    className="text-sm text-destructive"
                  >
                    {dict.nameRequired}
                  </p>
                ) : null}
              </TemplateOverviewField>

              <TemplateOverviewField
                id="template-description"
                label={dict.descriptionLabel}
              >
                <Textarea
                  id="template-description"
                  value={description}
                  onChange={(e) => onDescriptionChange(e.target.value)}
                  placeholder={dict.descriptionPlaceholder}
                  rows={3}
                  disabled={!canManage}
                  className="min-h-[5.5rem] min-w-0 w-full resize-none"
                />
              </TemplateOverviewField>
            </div>
          </TemplateOverviewSection>
        </div>

        <div
          className="min-w-0"
          style={
            lgTwoColumn && stammdatenCardHeight != null
              ? { height: stammdatenCardHeight }
              : undefined
          }
        >
          <TemplateTeamsAssignmentCard
            workspaceId={workspaceId}
            teamIds={templateTeamIds}
            canManage={canManage}
            dict={dict.detailTeams}
            onTeamIdsChange={onTeamIdsChange}
            variant="card"
            matchCardHeight={lgTwoColumn ? stammdatenCardHeight : undefined}
          />
        </div>
      </div>
    </div>
  );
}
